import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { DateTime } from 'luxon';
import IconSave from '../../components/icons/IconSave';
import language from '../../variables/language';

const BLANK = { activeProvider: 'GEMINI', geminiModel: '', openaiModel: '', groqModel: '', autoSwitchEnabled: false };
const BLANK_RATE_LIMITS = {
  GEMINI: { disabled: false, reason: null, disabledAt: null },
  OPENAI: { disabled: false, reason: null, disabledAt: null },
  GROQ: { disabled: false, reason: null, disabledAt: null },
};

// curated, real model ids as of this app's knowledge - the backend passes
// whatever string is stored straight through to each vendor's API
// (GeminiService/OpenAiService/GroqService), so this is a convenience list,
// not a validated enum. The current DB value is always included even if it
// isn't in this list (buildOptions below), so an unrecognized/custom value
// never silently vanishes from the select.
const GEMINI_MODELS = ['gemini-2.5-pro', 'gemini-2.5-flash', 'gemini-2.5-flash-lite', 'gemini-2.0-flash', 'gemini-2.0-flash-lite'];
const OPENAI_MODELS = ['gpt-5', 'gpt-5-mini', 'gpt-5-nano', 'gpt-4.1', 'gpt-4.1-mini', 'gpt-4o', 'gpt-4o-mini'];
// llama-3.3-70b-versatile/llama-3.1-8b-instant/llama3-70b-8192/llama3-8b-8192/
// mixtral-8x7b-32768/gemma2-9b-it (the previous list) were ALL deprecated by
// Groq - the llama-3.x pair specifically shut down 2026-08-16, per
// https://console.groq.com/docs/deprecations, confirmed live via a real
// GroqService call starting to fail. Current production chat models per
// https://console.groq.com/docs/models as of 2026-09-12 (verify again if this
// list ever goes stale - Groq deprecates fairly often):
const GROQ_MODELS = ['openai/gpt-oss-120b', 'openai/gpt-oss-20b', 'qwen/qwen3.6-27b'];

const buildOptions = (list, currentValue) => (
  currentValue && !list.includes(currentValue) ? [currentValue, ...list] : list
);

const flattenForm = (data) => ({
  activeProvider: data.activeProvider || 'GEMINI',
  geminiModel: data.geminiModel || '',
  openaiModel: data.openaiModel || '',
  groqModel: data.groqModel || '',
  autoSwitchEnabled: !!data.autoSwitchEnabled,
});

// the breaker is per-provider (see AiProviderConfig.java) so a Gemini outage
// can no longer block a switch to OpenAI/Groq, or vice versa - reflected
// here as independent status blocks, not one shared one.
const flattenRateLimits = (data) => ({
  GEMINI: {
    disabled: !!data.geminiRateLimitAutoDisabled,
    reason: data.geminiRateLimitDisabledReason || null,
    disabledAt: data.geminiRateLimitDisabledAt || null,
  },
  OPENAI: {
    disabled: !!data.openaiRateLimitAutoDisabled,
    reason: data.openaiRateLimitDisabledReason || null,
    disabledAt: data.openaiRateLimitDisabledAt || null,
  },
  GROQ: {
    disabled: !!data.groqRateLimitAutoDisabled,
    reason: data.groqRateLimitDisabledReason || null,
    disabledAt: data.groqRateLimitDisabledAt || null,
  },
});

const providerLabel = (provider, t) => {
  if (provider === 'GEMINI') return t.providerGemini;
  if (provider === 'OPENAI') return t.providerOpenai;
  if (provider === 'GROQ') return t.providerGroq;
  return provider;
};

const formatDate = (val) => {
  if (!val) return '';
  const dt = DateTime.fromISO(val);
  return dt.isValid ? dt.toFormat('dd.MM.yyyy HH:mm') : '';
};

function RateLimitStatus({ provider, state, onClear, clearing, t }) {
  if (!state.disabled) return '';
  return (
    <div className="ai-config-rate-limit-box">
      <div className="ai-config-rate-limit-text">
        <b>{t.rateLimited}</b>
        {state.disabledAt ? <span> — {t.rateLimitedSince} {formatDate(state.disabledAt)}</span> : ''}
        {state.reason ? <div className="ai-config-rate-limit-reason">{state.reason}</div> : ''}
      </div>
      <button type="button" className="ai-config-clear-btn" disabled={clearing} onClick={() => onClear(provider)}>
        {clearing ? t.clearingRateLimit : t.clearRateLimit}
      </button>
    </div>
  );
}

export default function AiProviderPanel() {
  const langRoot = language[language.lang];
  const t = langRoot.aiConfigPage;

  const [formValues, setFormValues] = useState(BLANK);
  const [initialValues, setInitialValues] = useState(BLANK);
  const [rateLimits, setRateLimits] = useState(BLANK_RATE_LIMITS);
  const [effectiveProvider, setEffectiveProvider] = useState(null);
  const [saving, setSaving] = useState(false);
  const [clearingProvider, setClearingProvider] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    axios.get('/api/ai-provider-config', { withCredentials: true })
      .then((response) => {
        setFormValues(flattenForm(response.data));
        setInitialValues(flattenForm(response.data));
        setRateLimits(flattenRateLimits(response.data));
        setEffectiveProvider(response.data.effectiveProvider || null);
        setStatus('done');
      })
      .catch(() => setStatus('done'));
  }, []);

  const isDirty = useMemo(() => JSON.stringify(formValues) !== JSON.stringify(initialValues), [formValues, initialValues]);

  const handleFieldChange = (name, value) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };
  const handleDiscard = () => setFormValues(initialValues);

  const handleSave = () => {
    if (saving) return;
    setSaving(true);
    axios.put('/api/ai-provider-config', JSON.stringify(formValues), {
      withCredentials: true,
      headers: { 'Content-Type': 'application/json' },
    }).then((response) => {
      setFormValues(flattenForm(response.data));
      setInitialValues(flattenForm(response.data));
      setRateLimits(flattenRateLimits(response.data));
      setEffectiveProvider(response.data.effectiveProvider || null);
    }).catch(() => {}).finally(() => setSaving(false));
  };

  const handleClearRateLimit = (provider) => {
    if (clearingProvider) return;
    setClearingProvider(provider);
    axios.post('/api/ai-provider-config/clear-rate-limit/' + provider, {}, { withCredentials: true })
      .then((response) => {
        setRateLimits(flattenRateLimits(response.data));
        setEffectiveProvider(response.data.effectiveProvider || null);
      })
      .catch(() => {})
      .finally(() => setClearingProvider(null));
  };

  if (status === 'loading') {
    return <p className="ai-config-empty">{langRoot['Loading ...']}</p>;
  }

  return (
    <div className="ai-config-panel">
      <label className="ai-config-field">
        <span>{t.activeProvider}</span>
        <select value={formValues.activeProvider} disabled={formValues.autoSwitchEnabled}
          onChange={(e) => handleFieldChange('activeProvider', e.target.value)}>
          <option value="GEMINI">{t.providerGemini}</option>
          <option value="OPENAI">{t.providerOpenai}</option>
          <option value="GROQ">{t.providerGroq}</option>
        </select>
      </label>

      <label className="ai-config-toggle-row">
        <input type="checkbox" checked={formValues.autoSwitchEnabled}
          onChange={(e) => handleFieldChange('autoSwitchEnabled', e.target.checked)} />
        <span>{t.autoSwitchEnabled}</span>
      </label>
      <p className="ai-config-auto-switch-hint">
        {formValues.autoSwitchEnabled ? t.autoSwitchHintOn : t.autoSwitchHintOff}
        {effectiveProvider ? (
          <> — {t.effectiveProviderLabel} <b>{providerLabel(effectiveProvider, t)}</b></>
        ) : ''}
      </p>

      <div className="ai-config-grid">
        <div className="ai-config-model-column">
          <label className="ai-config-field">
            <span>
              {t.geminiModel}
              {effectiveProvider === 'GEMINI' ? <i className="ai-config-active-badge">{t.activeBadge}</i> : ''}
            </span>
            <select value={formValues.geminiModel} onChange={(e) => handleFieldChange('geminiModel', e.target.value)}>
              {buildOptions(GEMINI_MODELS, formValues.geminiModel).map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <RateLimitStatus provider="GEMINI" state={rateLimits.GEMINI} onClear={handleClearRateLimit}
            clearing={clearingProvider === 'GEMINI'} t={t} />
        </div>
        <div className="ai-config-model-column">
          <label className="ai-config-field">
            <span>
              {t.openaiModel}
              {effectiveProvider === 'OPENAI' ? <i className="ai-config-active-badge">{t.activeBadge}</i> : ''}
            </span>
            <select value={formValues.openaiModel} onChange={(e) => handleFieldChange('openaiModel', e.target.value)}>
              {buildOptions(OPENAI_MODELS, formValues.openaiModel).map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <RateLimitStatus provider="OPENAI" state={rateLimits.OPENAI} onClear={handleClearRateLimit}
            clearing={clearingProvider === 'OPENAI'} t={t} />
        </div>
        <div className="ai-config-model-column">
          <label className="ai-config-field">
            <span>
              {t.groqModel}
              {effectiveProvider === 'GROQ' ? <i className="ai-config-active-badge">{t.activeBadge}</i> : ''}
            </span>
            <select value={formValues.groqModel} onChange={(e) => handleFieldChange('groqModel', e.target.value)}>
              {buildOptions(GROQ_MODELS, formValues.groqModel).map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <RateLimitStatus provider="GROQ" state={rateLimits.GROQ} onClear={handleClearRateLimit}
            clearing={clearingProvider === 'GROQ'} t={t} />
        </div>
      </div>

      {isDirty ? (
        <div className="ai-config-form-footer">
          <span className="ai-config-unsaved-hint">{langRoot.unsavedChanges}</span>
          <div className="ai-config-discard-link" onClick={handleDiscard}>{langRoot.cancel}</div>
          <button type="button" className="ai-config-save-btn" disabled={saving} onClick={handleSave}>
            <IconSave />
            {saving ? langRoot['Loading ...'] : langRoot.save}
          </button>
        </div>
      ) : ''}
    </div>
  );
}
